import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import get from 'lodash/get';
import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  FacilityApiFacilityIsPhoneNumberUpdateAllowedForIdGetRequest,
  FacilityApiFacilityOpenshiftOpenShiftIdPatchRequest,
  FacilityApiFacilityOpenshiftOpenShiftIdPendingPutRequest,
  FacilityApiFacilitySendMobileAppInvitePkEmployeeIdPostRequest,
  FacilityCacheKeys,
  OpenShiftApiOpenShiftPostedShiftEmployeesGetRequest,
  OpenShiftApiOpenShiftPostedShiftsGetRequest,
  useApi,
} from '../index';

const setOpenShiftAsPending =
  (api: Api) =>
  (
    facilityApiFacilityOpenshiftOpenShiftIdPendingPutRequest: FacilityApiFacilityOpenshiftOpenShiftIdPendingPutRequest
  ) =>
    api.facilityApi.facilityOpenshiftOpenShiftIdPendingPut(
      facilityApiFacilityOpenshiftOpenShiftIdPendingPutRequest
    );

const useUpdateOpenShiftsCache = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    queryClient.invalidateQueries([FacilityCacheKeys.OpenShiftsV2]).catch(noop);
    queryClient
      .invalidateQueries([FacilityCacheKeys.LegacyOpenShifts])
      .catch(noop);
    await queryClient
      .invalidateQueries([FacilityCacheKeys.OpenShiftsEmployeesV2])
      .catch(noop);
  }, [queryClient]);
};

export const useSetOpenShiftAsPending = () => {
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();
  const updateOpenShiftsCache = useUpdateOpenShiftsCache();

  return useMutation(setOpenShiftAsPending(api), {
    onSuccess: async () => {
      await updateOpenShiftsCache();
      notifySuccess(
        'Shift status updated to pending, please click on done button to finalize the assignment for all employees'
      );
    },
    onError: async (error) => {
      const message = get(error, 'message', '');
      notifyError(message);
    },
  });
};

const updateOpenShiftStatus =
  (api: Api) =>
  (
    facilityApiFacilityOpenshiftOpenShiftIdPatchRequest: FacilityApiFacilityOpenshiftOpenShiftIdPatchRequest
  ) => {
    return api.facilityApi.facilityOpenshiftOpenShiftIdPatch(
      facilityApiFacilityOpenshiftOpenShiftIdPatchRequest
    );
  };

export const useUpdateOpenShiftStatus = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const updateOpenShiftsCache = useUpdateOpenShiftsCache();

  return useMutation(updateOpenShiftStatus(api), {
    onSuccess: async () => {
      await updateOpenShiftsCache();
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useGetOpenShiftsV2 = (
  openShiftApiOpenShiftPostedShiftsGetRequest: OpenShiftApiOpenShiftPostedShiftsGetRequest,
  options?: { enabled?: boolean }
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      FacilityCacheKeys.OpenShiftsV2,
      openShiftApiOpenShiftPostedShiftsGetRequest,
    ],
    queryFn: () =>
      api.openShiftApi
        .openShiftPostedShiftsGet(openShiftApiOpenShiftPostedShiftsGetRequest)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled:
      Boolean(openShiftApiOpenShiftPostedShiftsGetRequest.departmentId) &&
      (options?.enabled ?? true),
  });
};

export type OpenShiftsV2ResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useGetOpenShiftsV2>['data']>
>;

export const useGetOpenShiftV2Employees = (
  openShiftApiOpenShiftPostedShiftEmployeesGetRequest: OpenShiftApiOpenShiftPostedShiftEmployeesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      FacilityCacheKeys.OpenShiftsEmployeesV2,
      openShiftApiOpenShiftPostedShiftEmployeesGetRequest,
    ],
    queryFn: () =>
      api.openShiftApi
        .openShiftPostedShiftEmployeesGet(
          openShiftApiOpenShiftPostedShiftEmployeesGetRequest
        )
        .then((response) => response.data ?? []),
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export type OpenShiftV2EmployeeItemResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useGetOpenShiftV2Employees>['data']>
>;

export const useLegacyOpenShiftsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [FacilityCacheKeys.LegacyOpenShifts],
    queryFn: () =>
      api.facilityApi
        .facilityOpenshiftGet({})
        .then((response) => response?.data?.openShifts ?? []),
    refetchOnWindowFocus: false,
  });
};

export type LegacyOpenShiftType = NonNullable<
  UnwrapArray<ReturnType<typeof useLegacyOpenShiftsGet>['data']>
>;

export type LegacyOpenShiftEmployeeType = NonNullable<
  UnwrapArray<LegacyOpenShiftType['employees']>
>;

const sendMobileInvite =
  (api: Api) =>
  async (
    body: FacilityApiFacilitySendMobileAppInvitePkEmployeeIdPostRequest
  ) => {
    return api.facilityApi
      .facilitySendMobileAppInvitePkEmployeeIdPost(body)
      .then((response) => response.data ?? []);
  };

export const useSendMobileInvite = () => {
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(sendMobileInvite(api), {
    onSuccess: async () => {
      notifySuccess('Mobile invite sent successfully');
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
